import React from 'react';
import { Box, IconButton, Slide, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { ACCOUNT_COLORS } from './accountPageStyles';

export type AccountToastVariant = 'success' | 'error' | 'warning';

export interface AccountToastState {
  variant: AccountToastVariant;
  title: string;
  message?: string;
}

/** Auth screens stay light even when the app uses `data-theme="dark"`. */
const LIGHT_TOAST_COLORS = {
  surface: '#FFFFFF',
  text: '#1B2233',
  textLo: '#6B7186',
  textDim: '#9BA0B2',
  surface2: '#F5F6F9',
} as const;

interface AccountPremiumToastProps {
  toast: AccountToastState | null;
  onClose: () => void;
  /** Wallet page uses top-right; default is top-center */
  placement?: 'top-center' | 'top-right';
  /** Force light card/text (login, signup) regardless of global theme tokens */
  appearance?: 'default' | 'light';
}

const placementSx = {
  'top-center': {
    top: { xs: 16, sm: 24 },
    left: '50%',
    right: 'auto',
    transform: 'translateX(-50%)',
    width: { xs: 'calc(100% - 32px)', sm: 'auto' },
  },
  'top-right': {
    top: { xs: 16, sm: 24 },
    right: { xs: 16, sm: 24 },
    left: 'auto',
    transform: 'none',
    width: { xs: 'calc(100% - 32px)', sm: 360 },
    maxWidth: { xs: 'calc(100% - 32px)', sm: 400 },
  },
} as const;

const variantStyles: Record<
  AccountToastVariant,
  { border: string; glow: string; iconBg: string; iconColor: string }
> = {
  success: {
    border: '1px solid rgba(31, 169, 113, 0.35)',
    glow: '0 12px 40px rgba(31, 169, 113, 0.18), 0 2px 8px rgba(15, 23, 42, 0.06)',
    iconBg: ACCOUNT_COLORS.successDim,
    iconColor: ACCOUNT_COLORS.success,
  },
  error: {
    border: '1px solid rgba(204, 27, 27, 0.32)',
    glow: '0 12px 40px rgba(204, 27, 27, 0.16), 0 2px 8px rgba(15, 23, 42, 0.06)',
    iconBg: ACCOUNT_COLORS.dangerBg,
    iconColor: ACCOUNT_COLORS.danger,
  },
  warning: {
    border: '1px solid rgba(224, 165, 55, 0.35)',
    glow: '0 12px 40px rgba(224, 165, 55, 0.16), 0 2px 8px rgba(15, 23, 42, 0.06)',
    iconBg: ACCOUNT_COLORS.warningDim,
    iconColor: ACCOUNT_COLORS.warning,
  },
};

export const AccountPremiumToast: React.FC<AccountPremiumToastProps> = ({
  toast,
  onClose,
  placement = 'top-center',
  appearance = 'default',
}) => {
  const open = Boolean(toast);
  const variant = toast?.variant ?? 'success';
  const styles = variantStyles[variant];
  const slideDirection = placement === 'top-right' ? 'left' : 'down';
  const palette =
    appearance === 'light'
      ? LIGHT_TOAST_COLORS
      : {
          surface: ACCOUNT_COLORS.surface,
          text: ACCOUNT_COLORS.text,
          textLo: ACCOUNT_COLORS.textLo,
          textDim: ACCOUNT_COLORS.textDim,
          surface2: ACCOUNT_COLORS.surface2,
        };

  return (
    <Slide direction={slideDirection} in={open} mountOnEnter unmountOnExit>
      <Box
        role="alert"
        aria-live="polite"
        sx={{
          position: 'fixed',
          zIndex: 1500,
          minWidth: { sm: 320 },
          pointerEvents: open ? 'auto' : 'none',
          ...placementSx[placement],
        }}
      >
        {toast && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              px: 2,
              py: 1.75,
              borderRadius: ACCOUNT_COLORS.radius,
              bgcolor: palette.surface,
              border: styles.border,
              boxShadow: styles.glow,
              backdropFilter: appearance === 'light' ? 'none' : 'blur(12px)',
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                bgcolor: styles.iconBg,
                color: styles.iconColor,
              }}
            >
              {variant === 'success' ? (
                <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />
              ) : variant === 'warning' ? (
                <WarningAmberOutlinedIcon sx={{ fontSize: 20 }} />
              ) : (
                <ErrorOutlineIcon sx={{ fontSize: 20 }} />
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 14, color: palette.text, lineHeight: 1.3 }}>
                {toast.title}
              </Typography>
              {toast.message && (
                <Typography sx={{ fontSize: 13, color: palette.textLo, mt: 0.5, lineHeight: 1.45 }}>
                  {toast.message}
                </Typography>
              )}
            </Box>
            <IconButton
              size="small"
              onClick={onClose}
              aria-label="Dismiss"
              sx={{
                mt: -0.25,
                mr: -0.5,
                color: palette.textDim,
                '&:hover': { bgcolor: palette.surface2 },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        )}
      </Box>
    </Slide>
  );
};
